
import { supabase } from "@/integrations/supabase/client";

export const getSignedUrl = async (path: string, bucket: string = 'exercise-gifs') => {
  if (!path) {
    console.log("No path provided to getSignedUrl");
    return null;
  }
  
  try {
    // Check if the path is already a complete URL
    if (path.startsWith('http')) {
      console.log("Path is already a URL:", path);
      return path;
    }
    
    console.log(`Getting signed URL for path: ${path} from bucket: ${bucket}`);
    
    // Try to get a signed URL first
    const { data: signedData, error: signedError } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 60 * 60 * 24); // 24 hours
    
    if (signedError) {
      console.error('Error getting signed URL:', signedError);
      console.log('Attempting to get public URL as fallback...');
      
      // Fallback to public URL
      const { data: publicData } = await supabase.storage
        .from(bucket)
        .getPublicUrl(path);
      
      if (publicData?.publicUrl) {
        console.log("Successfully got public URL:", publicData.publicUrl);
        return publicData.publicUrl;
      }
      
      console.error('Failed to get public URL');
      return null;
    }
    
    console.log("Successfully got signed URL for:", path);
    return signedData?.signedUrl;
  } catch (error) {
    console.error('Unexpected error in getSignedUrl:', error);
    return null;
  }
};

export interface ExerciseFilters {
  bodyPart?: string;
  equipment?: string;
  targetMuscle?: string;
  searchTerm?: string;
}

export type Exercise = {
  id: number;
  name: string;
  body_part: string;
  equipment?: string | null;
  target_muscle?: string | null;
  gif_url?: string | null;
  difficulty?: string | null;
  rating?: number | null;
  instructions?: string[];
  [key: string]: any;
};

export const getExercises = async (language: string, filters?: ExerciseFilters): Promise<Exercise[]> => {
  try {
    console.log(`Buscando exercícios com filtros: ${JSON.stringify(filters)}, language: ${language}`);
    
    // Select the appropriate table based on language
    let tableName: 'exercise_database' | 'exercise_database_en' | 'exercise_database_es' | 'exercise_database_fr' = 'exercise_database'; // Default table
    
    if (language === 'en') {
      tableName = 'exercise_database_en';
    } else if (language === 'es') {
      tableName = 'exercise_database_es';
    } else if (language === 'fr') {
      tableName = 'exercise_database_fr';
    }
    
    console.log(`Using table: ${tableName}`);
    
    // Iniciar a consulta básica
    const query = supabase.from(tableName)
      .select(`
        id, 
        name, 
        body_part, 
        equipment, 
        target_muscle, 
        gif_url, 
        difficulty, 
        rating,
        instruction_1, 
        instruction_2, 
        instruction_3, 
        instruction_4, 
        instruction_5, 
        instruction_6, 
        instruction_7, 
        instruction_8, 
        instruction_9, 
        instruction_10, 
        instruction_11
      `);
    
    // Aplicar filtros se fornecidos
    if (filters) {
      // Filtrar por parte do corpo
      if (filters.bodyPart && filters.bodyPart !== "_all") {
        query.eq('body_part', filters.bodyPart);
      }
      
      // Filtrar por equipamento
      if (filters.equipment && filters.equipment !== "_all") {
        query.eq('equipment', filters.equipment);
      }
      
      // Filtrar por músculo alvo
      if (filters.targetMuscle && filters.targetMuscle !== "_all") {
        query.eq('target_muscle', filters.targetMuscle);
      }
      
      // Filtrar por termo de pesquisa
      if (filters.searchTerm) {
        query.ilike('name', `%${filters.searchTerm}%`);
      }
    }
    
    // Executar a consulta
    const { data, error } = await query;
    
    // Verificar erros
    if (error) {
      console.error('Erro ao buscar exercícios:', error);
      return [];
    }
    
    console.log(`Encontrados ${data?.length || 0} exercícios.`);
    
    // Processar os dados para criar o array de instruções para cada exercício
    const processedData = data?.map(exercise => {
      // Coletar todas as instruções disponíveis em um array
      const instructions: string[] = [];
      for (let i = 1; i <= 11; i++) {
        const instruction = exercise[`instruction_${i}`];
        if (instruction) {
          instructions.push(instruction);
        }
      }
      
      // Use type assertion to tell TypeScript that exercise has the right shape
      return {
        ...exercise,
        instructions: instructions.length > 0 ? instructions : undefined
      } as Exercise;
    }) || [];
    
    // Retornar dados processados ou array vazio
    return processedData;
  } catch (error) {
    console.error('Erro em getExercises:', error);
    return [];
  }
};
